import type { Nullable } from "@iot-platform/core";
import { LanguageIsoCodeEnumMapper } from "@iot-platform/core";
import type { CurrentUserDto } from "@user/data/dto/current-user.dto";
import { CurrentUserDtoSchema } from "@user/data/dto/current-user.dto";
import { UserRoleEnumMapper } from "@user/data/mappers/user-role-enum.mapper";
import type {
    AuthenticatedUser,
    AuthenticatedUserInputProps,
} from "@user/domain/models/authenticated-user.model";
import { AuthenticatedUserSchema } from "@user/domain/models/authenticated-user.model";
import { inject, injectable } from "inversify";

@injectable()
export class AuthenticatedUserMapper {
    constructor(
        @inject(UserRoleEnumMapper)
        private readonly userRoleEnumMapper: UserRoleEnumMapper,
        @inject(LanguageIsoCodeEnumMapper)
        private readonly languageIsoCodeEnumMapper: LanguageIsoCodeEnumMapper,
    ) {}
    map(currentUserDto: CurrentUserDto): Nullable<AuthenticatedUser> {
        const currentUserDtoParsed =
            CurrentUserDtoSchema.safeParse(currentUserDto);

        if (!currentUserDtoParsed.success) {
            console.error(currentUserDtoParsed.error.issues);
            return null;
        }

        const userRole = this.userRoleEnumMapper.map(currentUserDto.roles?.[0]);

        const language = currentUserDto.locale
            ? this.languageIsoCodeEnumMapper.map(currentUserDto.locale)
            : undefined;

        const maybeAuthenticatedUser = AuthenticatedUserSchema.safeParse({
            id: currentUserDto.id,
            name: currentUserDto.username ?? "",
            email: currentUserDto.email,
            rol: currentUserDto.roles?.length ? userRole : null,
            language,
            timeZone: currentUserDto.time_zone,
        } satisfies AuthenticatedUserInputProps);

        return maybeAuthenticatedUser.success
            ? maybeAuthenticatedUser.data
            : null;
    }
}
